/**
 * Вводный тур для нового ученика: четыре карточки о главном в приложении —
 * Нейросеть, Карман, чат и звонок с Тико, сервисы (Справочник и «Слова с Тико»).
 *
 * Показывается один раз, сразу после первого входа (`TutorialGate`,
 * флаг `hasSeenTutorial` в базе): и «Пропустить», и последняя кнопка
 * закрывают тур навсегда. Каждая
 * карточка — плитка в тоне сервиса из палитры приложения, крупная иконка и
 * два предложения. Листается кнопками, стрелками и точками; Esc — пропуск.
 */

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button';
import { IconArrowLeft, IconArrowRight, IconBrain, IconCards, IconLayoutGrid, IconMessageCircle, IconSparkles, type AppIcon } from '../icons';
import { SERVICE_TONES, type ServiceTone } from '../services/ServicesGrid';

type StepKey = 'brain' | 'pocket' | 'chat' | 'services';

interface Step {
  readonly key: StepKey;
  readonly icon: AppIcon;
  readonly tone: ServiceTone;
}

const STEPS: readonly Step[] = [
  { key: 'brain', icon: IconBrain, tone: 'cocoa' },
  { key: 'pocket', icon: IconCards, tone: 'sand' },
  { key: 'chat', icon: IconMessageCircle, tone: 'teal' },
  { key: 'services', icon: IconLayoutGrid, tone: 'cocoa' },
];

export function TutorialModal({ onClose }: { readonly onClose: () => void }) {
  const { t } = useTranslation();
  const still = useReducedMotion() === true;
  const [index, setIndex] = useState(0);
  // Направление листания: новая карточка въезжает с той стороны, куда идём.
  const [direction, setDirection] = useState(1);
  const step = STEPS[index] ?? STEPS[0];
  const last = index === STEPS.length - 1;

  const go = useCallback((target: number) => {
    setIndex((current) => {
      const next = Math.min(Math.max(target, 0), STEPS.length - 1);
      setDirection(next >= current ? 1 : -1);
      return next;
    });
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') go(index + 1);
      if (event.key === 'ArrowLeft') go(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, index, onClose]);

  if (step === undefined) return null;
  const tone = SERVICE_TONES[step.tone];
  const Icon = step.icon;

  // Портал в body: внутри страницы оверлей обрезала бы трансформация
  // секции, а шапка и нижнее меню оставались бы поверх тура.
  return createPortal(
    <motion.div
      className="fixed inset-0 z-[100] grid place-items-center overflow-y-auto bg-heading/30 p-4 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={t('tutorial.dialog')}
        initial={still ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 360, damping: 30 }}
        className="relative my-auto flex w-full max-w-md flex-col overflow-hidden rounded-card border border-line bg-[#FFFCF6] shadow-card-lg dark:bg-[#2B231D]"
      >
        {/* Иллюстрация шага: плитка в тоне сервиса и крупная иконка. */}
        <div className="relative h-52 overflow-hidden sm:h-60" style={{ background: tone.gradient }}>
          <span aria-hidden="true" className="absolute -left-10 -top-12 h-44 w-44 rounded-full bg-white/20 blur-2xl" />
          <span aria-hidden="true" className="absolute -bottom-16 right-0 h-48 w-48 rounded-full bg-black/10 blur-2xl" />
          <AnimatePresence mode="popLayout" initial={false} custom={direction}>
            <motion.div
              key={step.key}
              initial={still ? { opacity: 0 } : { opacity: 0, x: 60 * direction, rotate: 6 * direction }}
              animate={{ opacity: 1, x: 0, rotate: 0 }}
              exit={still ? { opacity: 0 } : { opacity: 0, x: -60 * direction, rotate: -6 * direction }}
              transition={{ type: 'spring', stiffness: 300, damping: 26 }}
              className="absolute inset-0 grid place-items-center"
            >
              <span className={`grid h-28 w-28 place-items-center rounded-[2rem] border border-white/40 shadow-card-lg backdrop-blur-md sm:h-32 sm:w-32 ${tone.glass} ${tone.text}`}>
                <Icon size={60} stroke={1.6} aria-hidden="true" />
              </span>
            </motion.div>
          </AnimatePresence>
          <button
            type="button"
            onClick={onClose}
            className={`absolute right-3 top-3 rounded-full px-3 py-1.5 text-sm font-semibold backdrop-blur-md transition hover:bg-white/40 ${tone.glass} ${tone.text}`}
          >
            {t('tutorial.skip')}
          </button>
        </div>

        <div className="flex flex-col gap-5 p-6 sm:p-7">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step.key}
              initial={still ? { opacity: 0 } : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="flex min-h-[7.5rem] flex-col gap-2"
              aria-live="polite"
            >
              <p className="font-heading text-xs font-extrabold uppercase tracking-wider text-teal-deep dark:text-teal">
                {t('tutorial.counter', { index: index + 1, count: STEPS.length })}
              </p>
              <h2 className="t-h1">{t(`tutorial.steps.${step.key}.title`)}</h2>
              <p className="text-body leading-relaxed text-muted">{t(`tutorial.steps.${step.key}.text`)}</p>
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-center gap-2" role="tablist" aria-label={t('tutorial.dots')}>
            {STEPS.map((item, dot) => (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={dot === index}
                aria-label={t('tutorial.goTo', { index: dot + 1 })}
                onClick={() => go(dot)}
                className={`h-2 rounded-full transition-all duration-300 ${dot === index ? 'w-6 bg-accent' : 'w-2 bg-heading/20 hover:bg-heading/40'}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-3">
            {index > 0 && (
              <Button variant="ghost" icon={IconArrowLeft} onClick={() => go(index - 1)}>
                {t('tutorial.back')}
              </Button>
            )}
            <Button
              className="flex-1"
              fullWidth
              autoFocus
              icon={last ? IconSparkles : IconArrowRight}
              onClick={last ? onClose : () => go(index + 1)}
            >
              {last ? t('tutorial.finish') : t('tutorial.next')}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}
